import Cookies from "js-cookie";

const CSRF_TOKEN_SELECTOR = 'meta[name="csrf-token"]';
const CSRF_COOKIE_NAME = "csrf_token";

const metaCsrfToken = (): string | undefined =>
  document.querySelector<HTMLMetaElement>(CSRF_TOKEN_SELECTOR)?.content;

const cookieCsrfToken = (): string | undefined => {
  try {
    return Cookies.get(CSRF_COOKIE_NAME);
  } catch {
    // Opaque sandboxed documents can reject `document.cookie` access.
    return undefined;
  }
};

// Cookie-first, so every consumer (the jsonFetch header, CircleAi, onboarding,
// …) reads the token the server refreshes on each Internal API response rather
// than the baked meta tag, which goes stale when the session token rotates
// (login, multi-tab, long-lived sessions). Falls back to the meta tag for the
// first request, before any response has set the cookie.
export const csrfToken = (): string | undefined =>
  cookieCsrfToken() ?? metaCsrfToken();

const writeCsrfCookie = (token: string): void => {
  // Mirror the `_circle_session` cookie's attributes so the readable token is
  // sent wherever the session is — incl. cross-site iframes: `SameSite=None;
  // Secure` over HTTPS, `Lax` over plain HTTP (dev, where Secure/None is invalid).
  const isSecure = window.location.protocol === "https:";
  try {
    Cookies.set(CSRF_COOKIE_NAME, token, {
      path: "/",
      sameSite: isSecure ? "none" : "lax",
      secure: isSecure,
    });
  } catch {
    // Keep the meta token usable when the browser rejects cookie writes.
  }
};

export const setCsrfToken = (token?: string): void => {
  if (!token) return;

  // Update the cookie and the meta tag, which is still read by @rails/ujs.
  writeCsrfCookie(token);

  const csrfMetaTag =
    document.querySelector<HTMLMetaElement>(CSRF_TOKEN_SELECTOR);
  if (csrfMetaTag) {
    csrfMetaTag.content = token;
  }
};

export const csrfTokenHeader = (): Record<string, string> => {
  const token = csrfToken();
  return token ? { "X-CSRF-Token": token } : {};
};

// Seed the cookie from the freshly server-rendered `<meta>` token on load, so a
// stale `csrf_token` cookie left over from a previous session (e.g. after a
// full-page login that rotated the session token) can't shadow it for the
// cookie-first reads above. Runs synchronously at import — before roots that
// mount React synchronously (e.g. checkout, which fires a mutation on first
// render) issue any request — which a `DOMContentLoaded` hook would miss.
if (typeof document !== "undefined") {
  const meta = metaCsrfToken();
  if (meta) writeCsrfCookie(meta);
}
